import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Sparkles, Flame, Trophy, PanelLeftClose, PanelLeft, LogIn, LogOut, Settings, Trash2 } from "lucide-react";
import { STAGES, statusToIndex, timeAgo, C } from "../lib/theme";
import { googleLogin, logout } from "../lib/auth";
import { api } from "../lib/api";
import SettingsDialog from "./SettingsDialog";

function StageDot({ status }) {
  if (status === "mastered") {
    return <span className="rounded-full px-1.5 py-0.5 font-mono text-[9px]" style={{ background: `${C.grn}22`, color: C.grn }}>Mastered</span>;
  }
  const s = STAGES[statusToIndex(status)] || STAGES[0];
  return (
    <span className="flex items-center gap-1 font-mono text-[10px] text-muted">
      <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
      {s.name}
    </span>
  );
}

export default function Sidebar({ sessions, user, activeId, open, onClose, onDeleted }) {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [query, setQuery] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const searchRef = useRef(null);

  const handleDelete = async (e, s) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${s.topic}"? This can't be undone.`)) return;
    try {
      await api.deleteSession(s.id);
      onDeleted && onDeleted(s.id);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const filtered = (sessions || []).filter((s) => s.topic.toLowerCase().includes(query.toLowerCase()));

  if (collapsed) {
    return (
      <div className="hidden lg:flex w-14 shrink-0 flex-col items-center border-r border-hair bg-surface py-4">
        <button onClick={() => setCollapsed(false)} className="rounded-md p-2 text-muted hover:bg-white/5 hover:text-ink" data-testid="sidebar-expand">
          <PanelLeft className="h-5 w-5" />
        </button>
        <button onClick={() => navigate("/")} className="mt-4 rounded-md p-2 text-blu hover:bg-white/5" title="New topic">
          <Plus className="h-5 w-5" />
        </button>
      </div>
    );
  }

  return (
    <>
      {open && <div className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden" onClick={onClose} />}
      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 z-40 flex h-full w-72 flex-col border-r border-hair bg-surface transition-transform duration-300 lg:static lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-left" data-testid="logo">
            <div className="flex h-8 w-8 items-center justify-center rounded-md" style={{ background: `${C.blu}1f`, border: `1px solid ${C.blu}55` }}>
              <span className="font-display text-lg" style={{ color: C.blu }}>F</span>
            </div>
            <div>
              <div className="font-display text-xl leading-none">Fathom</div>
              <div className="font-mono text-[8px] uppercase tracking-[0.15em] text-muted">learn it until you can teach it</div>
            </div>
          </button>
          <div className="flex items-center gap-1">
            <button onClick={() => setSettingsOpen(true)} className="rounded-md p-1.5 text-muted hover:bg-white/5 hover:text-ink" data-testid="open-settings-btn" title="Settings">
              <Settings className="h-4 w-4" />
            </button>
            <button onClick={() => setCollapsed(true)} className="hidden rounded-md p-1.5 text-muted hover:bg-white/5 hover:text-ink lg:block" data-testid="sidebar-collapse">
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        </div>

        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

        <div className="px-4">
          <button
            data-testid="new-topic-btn"
            onClick={() => navigate("/")}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-blu py-2.5 text-sm font-semibold text-bg transition-transform hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" /> New topic
          </button>
        </div>

        <div className="px-4 py-3">
          <div className="flex items-center gap-2 rounded-md border border-hair bg-surface2 px-3 py-2">
            <Search className="h-4 w-4 text-muted" />
            <input
              ref={searchRef}
              data-testid="sidebar-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your topics"
              className="w-full bg-transparent text-sm text-ink placeholder:text-muted focus:outline-none"
            />
            <span className="font-mono text-[10px] text-muted">⌘K</span>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2">
          <div className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-muted">Recent</div>
          {(!filtered || filtered.length === 0) && (
            <div className="px-2 py-3 text-xs text-muted">{query ? "No topics match." : "No topics yet. Start one to see it here."}</div>
          )}
          {filtered && filtered.map((s) => (
            <div key={s.id} className="group relative">
              <button
                data-testid={`recent-item-${s.id}`}
                onClick={() => { navigate(`/s/${s.id}`); onClose && onClose(); }}
                className={`mb-1 flex w-full flex-col gap-1 rounded-md px-2 py-2 pr-8 text-left transition-colors hover:bg-white/5 ${activeId === s.id ? "bg-white/5" : ""}`}
              >
                <span className="line-clamp-1 text-sm text-ink">{s.topic}</span>
                <span className="flex items-center justify-between">
                  <StageDot status={s.status} />
                  <span className="font-mono text-[10px] text-muted">
                    {s.attempt > 1 && s.status !== "mastered" ? `Attempt ${s.attempt} · ` : ""}{timeAgo(s.updatedAt)}
                  </span>
                </span>
              </button>
              <button
                data-testid={`delete-item-${s.id}`}
                onClick={(e) => handleDelete(e, s)}
                title="Delete topic"
                className="press absolute right-1 top-2.5 rounded-md p-1.5 text-muted opacity-0 transition-opacity hover:bg-ros/15 hover:text-ros focus:opacity-100 group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>

        <div className="m-3 rounded-lg border border-hair bg-surface2 p-3" data-testid="user-card">
          <div className="flex items-center gap-2">
            {user?.picture ? (
              <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: `${C.vio}22` }}>
                <Sparkles className="h-4 w-4" style={{ color: C.vio }} />
              </div>
            )}
            <span className="line-clamp-1 text-sm font-medium text-ink">{user?.displayName || "Learner"}</span>
          </div>
          <div className="mt-3 flex gap-2">
            <div className="flex-1 rounded-md bg-surface px-2 py-1.5">
              <div className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-muted"><Trophy className="h-3 w-3" style={{ color: C.grn }} /> Mastered</div>
              <div className="font-mono text-lg tab-nums" style={{ color: C.grn }}>{user?.topicsMastered ?? 0}</div>
            </div>
            <div className="flex-1 rounded-md bg-surface px-2 py-1.5">
              <div className="flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-muted"><Flame className="h-3 w-3" style={{ color: C.amb }} /> Streak</div>
              <div className="font-mono text-lg tab-nums" style={{ color: C.amb }}>{user?.streak ?? 0}</div>
            </div>
          </div>
          {user?.authed ? (
            <button data-testid="sign-out-btn" onClick={logout} className="press mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-hair py-2 text-xs text-muted hover:text-ink">
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          ) : (
            <button data-testid="sign-in-btn" onClick={googleLogin} className="press mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-hair py-2 text-xs text-ink hover:bg-white/5">
              <LogIn className="h-3.5 w-3.5" /> Sign in with Google
            </button>
          )}
        </div>
      </aside>
    </>
  );
}
